import React, { useEffect, useState } from "react";
import { Flame, CheckCircle, Sparkles, Loader2 } from "lucide-react";

interface LoginSuccessPageProps {
  onNavigate: (path: string) => void;
  apiFetch: (url: string, init?: RequestInit) => Promise<Response>;
}

export const LoginSuccessPage: React.FC<LoginSuccessPageProps> = ({
  onNavigate,
  apiFetch
}) => {
  const [statusText, setStatusText] = useState("Syncing your daily discovery quests and streak...");
  const [isDone, setIsDone] = useState(false);

  useEffect(() => {
    let mounted = true;

    const performLoginSync = async () => {
      try {
        const res = await apiFetch("/api/quests/daily-check", { method: "POST" });
        if (res.ok) {
          const data = await res.json();
          if (mounted && data.stats) {
            setStatusText(`Streak updated: ${data.stats.daily_streak || 1} day(s) active!`);
          }
        }
      } catch (err) {
        console.warn("Daily check during login-success encountered exception:", err);
      } finally {
        if (mounted) {
          setIsDone(true);
          setTimeout(() => {
            if (mounted) {
              const target = sessionStorage.getItem("auth_redirect_target");
              if (target && (target.startsWith("/business/") || target.includes("sharedBusinessId="))) {
                sessionStorage.removeItem("auth_redirect_target");
                onNavigate(target);
              } else {
                onNavigate("/dashboard");
              }
            }
          }, 600);
        }
      }
    };

    performLoginSync();

    return () => {
      mounted = false;
    };
  }, [apiFetch, onNavigate]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white p-6">
      <div className="w-full max-w-md p-8 bg-white dark:bg-slate-800/90 border border-slate-200 dark:border-slate-700/80 rounded-2xl shadow-xl text-center space-y-6 animate-in fade-in zoom-in-95 duration-300">
        <div className="mx-auto w-16 h-16 rounded-full bg-amber-500/20 border border-amber-500/40 flex items-center justify-center text-amber-400">
          {isDone ? (
            <CheckCircle className="w-8 h-8 text-emerald-400 animate-in zoom-in duration-200" />
          ) : (
            <Flame className="w-8 h-8 text-amber-400 animate-pulse" />
          )}
        </div>

        <div className="space-y-2">
          <h2 className="text-xl font-bold tracking-tight text-white flex items-center justify-center gap-2">
            Welcome Back to BizNearYou
            <Sparkles className="w-5 h-5 text-amber-400" />
          </h2>
          <p className="text-sm text-slate-300 font-medium">
            {statusText}
          </p>
        </div>

        <div className="pt-2 flex justify-center items-center gap-2 text-xs text-slate-400">
          {!isDone && <Loader2 className="w-4 h-4 animate-spin text-amber-400" />}
          <span>Redirecting to your workspace...</span>
        </div>
      </div>
    </div>
  );
};
